import type { CATInlineCode } from "./translation.types"

const INLINE_TAG_PATTERN = /⟦\d+⟧/g
const LITERAL_INLINE_MARKER_PATTERN = /⟦(\\+)(\d+)⟧/g
const MARKER_SHAPED_TEXT_PATTERN = /⟦(\\*)(\d+)⟧/g

const decodeLiteralInlineMarkers = (value: string) =>
  value.replace(
    LITERAL_INLINE_MARKER_PATTERN,
    (_match, backslashes: string, digits: string) =>
      `⟦${backslashes.slice(1)}${digits}⟧`
  )

export const encodeLiteralInlineMarkers = (value: string) =>
  value.replace(
    MARKER_SHAPED_TEXT_PATTERN,
    (_match, backslashes: string, digits: string) =>
      `⟦\\${backslashes}${digits}⟧`
  )

export interface ParsedInlineText {
  textParts: string[]
  inlineTags: string[]
}

export const parseInlineText = (value: string): ParsedInlineText => {
  const textParts: string[] = []
  const inlineTags: string[] = []
  let textStart = 0

  for (const match of value.matchAll(INLINE_TAG_PATTERN)) {
    const index = match.index ?? 0
    textParts.push(decodeLiteralInlineMarkers(value.slice(textStart, index)))
    inlineTags.push(match[0])
    textStart = index + match[0].length
  }
  textParts.push(decodeLiteralInlineMarkers(value.slice(textStart)))

  return { textParts, inlineTags }
}

export const hasExactInlineTags = (value: string, expectedTags: string[]) => {
  const actualTags = parseInlineText(value).inlineTags
  return (
    actualTags.length === expectedTags.length &&
    actualTags.every((tag, index) => tag === expectedTags[index])
  )
}

export const hasValidInlineCodeTopology = (
  value: string,
  expectedTags: string[],
  inlineCodes: CATInlineCode[] | undefined
) => {
  if (!hasExactInlineTags(value, expectedTags)) return false
  if (inlineCodes === undefined) return true
  if (
    inlineCodes.length !== expectedTags.length ||
    inlineCodes.some((code, index) => code.marker !== expectedTags[index])
  ) {
    return false
  }

  const openPairs: string[] = []
  const seenPairIds = new Set<string>()
  for (const code of inlineCodes) {
    if (code.kind === "standalone") {
      if (code.pairId != null) return false
      continue
    }
    if (!code.pairId) return false
    if (code.kind === "paired_start") {
      if (seenPairIds.has(code.pairId)) return false
      seenPairIds.add(code.pairId)
      openPairs.push(code.pairId)
      continue
    }
    if (openPairs.pop() !== code.pairId) return false
  }
  return openPairs.length === 0
}

export const isInternalInlineBoundary = (
  code: CATInlineCode | undefined
) =>
  code?.kind === "standalone" &&
  (code.role === "run_boundary" || code.role === "style_boundary")

export const getInlineCodeDisplay = (
  tag: string,
  code: CATInlineCode | undefined
) => {
  if (!code || code.marker !== tag) {
    return { kind: "unknown" as const, label: tag }
  }
  const identity = code.pairId || code.codeId
  if (code.kind === "paired_start") {
    return { kind: code.kind, label: `〈${identity}` }
  }
  if (code.kind === "paired_end") {
    return { kind: code.kind, label: `${identity}〉` }
  }
  if (isInternalInlineBoundary(code)) {
    return { kind: code.kind, label: "│" }
  }
  return { kind: code.kind, label: `◆${identity}` }
}

export const stripInlineTags = (value: string) =>
  parseInlineText(value).textParts.join("")

export const hasProtectedInlineProjection = (
  inlineTags: string[] | undefined
): inlineTags is string[] => inlineTags !== undefined

export const mustProtectTmApplication = (
  inlineTags: string[] | undefined,
  inlineTagsValid: boolean
) => !inlineTagsValid || (inlineTags?.length ?? 0) > 0

export const buildTmPropagationExclusionIds = (
  segments: {
    id: string
    inlineTags?: string[]
    inlineTagsValid: boolean
    locked?: boolean
  }[],
  pendingSegmentIds: string[]
) => {
  const excludedSegmentIds = new Set(pendingSegmentIds)
  segments.forEach((segment) => {
    if (
      segment.locked ||
      mustProtectTmApplication(segment.inlineTags, segment.inlineTagsValid)
    ) {
      excludedSegmentIds.add(segment.id)
    }
  })
  return Array.from(excludedSegmentIds)
}

export const getInlineDisplayText = (
  value: string,
  inlineTags: string[] | undefined
) => (hasProtectedInlineProjection(inlineTags) ? stripInlineTags(value) : value)

export interface InlineProjection {
  text: string
  inlineTags: string[] | undefined
  inlineCodes?: CATInlineCode[]
}

export const getReadonlyInlineProjection = ({
  plainText,
  taggedText,
  inlineTags,
  inlineCodes,
}: {
  plainText: string
  taggedText: string | null | undefined
  inlineTags: string[] | undefined
  inlineCodes?: CATInlineCode[]
}): InlineProjection => {
  if (
    typeof taggedText !== "string" ||
    !inlineTags?.length ||
    !hasExactInlineTags(taggedText, inlineTags)
  ) {
    return { text: plainText, inlineTags: undefined }
  }
  return {
    text: taggedText,
    inlineTags,
    ...(inlineCodes === undefined ? {} : { inlineCodes }),
  }
}

export const getSourceDisplayInlineProjection = ({
  plainText,
  displayTaggedText,
  displayInlineTags,
  displayInlineCodes,
  protectedTaggedText,
  protectedInlineTags,
  protectedInlineCodes,
}: {
  plainText: string
  displayTaggedText: string | null | undefined
  displayInlineTags: string[] | undefined
  displayInlineCodes?: CATInlineCode[]
  protectedTaggedText: string | null | undefined
  protectedInlineTags: string[] | undefined
  protectedInlineCodes?: CATInlineCode[]
}): InlineProjection => {
  if (
    typeof displayTaggedText === "string" &&
    displayInlineTags !== undefined &&
    hasExactInlineTags(displayTaggedText, displayInlineTags)
  ) {
    return {
      text: displayTaggedText,
      inlineTags: displayInlineTags,
      ...(displayInlineCodes === undefined
        ? {}
        : { inlineCodes: displayInlineCodes }),
    }
  }

  return getReadonlyInlineProjection({
    plainText,
    taggedText: protectedTaggedText,
    inlineTags: protectedInlineTags,
    inlineCodes: protectedInlineCodes,
  })
}

export const replaceInlineTextPart = ({
  value,
  expectedTags,
  partIndex,
  nextText,
}: {
  value: string
  expectedTags: string[]
  partIndex: number
  nextText: string
}) => {
  const parsed = parseInlineText(value)
  if (
    !hasExactInlineTags(value, expectedTags) ||
    partIndex < 0 ||
    partIndex >= parsed.textParts.length
  ) {
    return value
  }

  parsed.textParts[partIndex] = nextText
  return parsed.textParts
    .map(
      (part, index) =>
        `${encodeLiteralInlineMarkers(part)}${expectedTags[index] ?? ""}`
    )
    .join("")
}

export interface InlineTextReplacementResult {
  value: string
  count: number
}

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

export const replaceTextPreservingInlineTags = ({
  value,
  inlineTags,
  search,
  replacement,
  replaceAll = true,
}: {
  value: string
  inlineTags?: string[]
  search: string
  replacement: string
  replaceAll?: boolean
}): InlineTextReplacementResult => {
  if (!search) return { value, count: 0 }

  const replacePart = (part: string, remaining: boolean) => {
    if (!remaining) return { part, count: 0 }
    let count = 0
    const expression = new RegExp(
      escapeRegExp(search),
      replaceAll ? "giu" : "iu"
    )
    return {
      part: part.replace(expression, () => {
        count += 1
        return replacement
      }),
      count,
    }
  }

  if (!hasProtectedInlineProjection(inlineTags)) {
    const result = replacePart(value, true)
    return { value: result.part, count: result.count }
  }
  if (!hasExactInlineTags(value, inlineTags)) return { value, count: 0 }

  const parsed = parseInlineText(value)
  let count = 0
  const nextParts = parsed.textParts.map((part) => {
    const result = replacePart(part, replaceAll || count === 0)
    count += result.count
    return result.part
  })
  return {
    value: nextParts
      .map(
        (part, index) =>
          `${encodeLiteralInlineMarkers(part)}${inlineTags[index] ?? ""}`
      )
      .join(""),
    count,
  }
}
